import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { useBrand, useMarket, useTenantPath, useTenantSlug } from "@/lib/brand";
import { api, mediaUrl } from "@/lib/api";
import { buildLocalBusinessJsonLd } from "@/lib/seo";

/**
 * AgencyGuides — the tenant's public guides/articles index. Fully
 * data-driven off whatever the agent has published via the CRM's
 * Guides tool, so this works for any Signature tenant with zero setup —
 * an agent who never writes a guide just gets an empty section, not a
 * broken page.
 */
export default function AgencyGuides() {
  const { slug: urlSlug } = useParams();
  const brand = useBrand();
  const isUs = useMarket() === "us";
  const tenantPath = useTenantPath();
  const biz = brand.business || {};
  const { slug: tenantSlug, known: tenantKnown } = useTenantSlug(urlSlug);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Hold until the tenant is known: on a custom domain the slug arrives
    // with the brand. See useTenantSlug.
    if (!tenantKnown) return;
    api.get(`/crm/public/guides?tenant=${tenantSlug}`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tenantSlug, tenantKnown]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title={biz.name ? `Guides & Advice, ${biz.name} | ${brand.regions_covered_prose || "Estate Agents"}` : undefined}
        description={`Practical buying, selling and ${isUs ? "renting" : "letting"} guides from ${biz.name || "our team"}, covering ${brand.regions_covered_prose || "your area"}.`}
        image={brand.assets?.hero_image_url ? mediaUrl(brand.assets.hero_image_url) : undefined}
        jsonLd={buildLocalBusinessJsonLd(brand)}
      />
      <SiteHeader />
      <main className="flex-1">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 py-14">
          <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">Guides</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-primary mt-3">Guides &amp; advice</h1>
          <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">
            Practical, honest advice on buying, selling, {isUs ? "renting" : "letting"} and moving, from {biz.name || "our team"}.
          </p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {loading ? (
              <div className="col-span-full text-center text-muted-foreground py-10">Loading…</div>
            ) : items.length === 0 ? (
              <div className="col-span-full text-center text-muted-foreground py-10 border border-dashed border-border rounded-sm">
                No guides published yet. Check back soon.
              </div>
            ) : items.map((g) => (
              <Link
                key={g.slug}
                to={tenantPath(`guides/${g.slug}`)}
                data-testid={`guide-card-${g.slug}`}
                className="group border border-border bg-card rounded-sm p-6 hover:border-primary transition-colors"
              >
                {g.category && (
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{g.category}</div>
                )}
                <h2 className="font-serif-display text-2xl text-primary mt-2 group-hover:text-primary/80">{g.title}</h2>
                {g.excerpt && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{g.excerpt}</p>}
                <div className="text-xs uppercase tracking-widest text-primary mt-4 flex items-center gap-1">
                  Read guide <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
